<script setup lang="ts">
import { computed } from 'vue';
import { getTimeZones, type TimeZone } from '@vvo/tzdb';
import Combobox from './Combobox.vue';
import { Badge } from './badge';
import { ianaTimeZoneCurrentGet } from '../cross-app-shared/services/dates-and-durations.ts';

// The template has two roots, so attributes like `disabled` are forwarded to the picker explicitly.
defineOptions({ inheritAttrs: false });

const props = defineProps<{
  /** The time zone used when none is chosen, shown as a hint below the picker. */
  timeZoneFallback: string | null;
}>();

const timeZone = defineModel<string | null>({ required: true });

function ianaTimeZoneCurrentTime(timeZone: string, now = new Date()): string {
  return Intl.DateTimeFormat(undefined, { timeStyle: 'short', timeZone }).format(now);
}

function ianaTimeZoneOptions(): { timeZones: TimeZone[]; defaultTimeZone: string } {
  let defaultTimeZone = ianaTimeZoneCurrentGet();

  return {
    timeZones: getTimeZones().filter((tz) => {
      const isInNA = tz.continentName === 'North America';

      if (isInNA && tz.group.includes(defaultTimeZone)) {
        defaultTimeZone = tz.name;
      }

      return isInNA;
    }),
    defaultTimeZone,
  };
}

const { timeZones: timeZonesFull, defaultTimeZone } = ianaTimeZoneOptions();
type TimeZoneSelectOption = {
  /** Also the text that search filters against, e.g. "-05:00 Eastern Time - New York, Detroit, ..." */
  label: string;
  value: string;

  alternativeName: string;
  city: string;
  mainCities: string;
  offset: string;
};
function timeZoneSelectOption(tz: TimeZone): TimeZoneSelectOption {
  return {
    label: tz.currentTimeFormat,
    alternativeName: tz.alternativeName,
    value: tz.name,
    city: tz.mainCities[0],
    mainCities: tz.mainCities.join(', '),
    offset: tz.currentTimeFormat.split(' ')[0],
  };
}
const timeZones = timeZonesFull.map(timeZoneSelectOption);

// Searches every zone, not just the North American ones offered in the picker, since the fallback can be a territory
// like Guam. Matching on the group also resolves aliases to the canonical zone.
const timeZoneFallbackOption = computed((): TimeZoneSelectOption | null => {
  const fallback = props.timeZoneFallback;
  if (fallback === null) {
    return null;
  }
  const tz = getTimeZones().find((tz) => tz.name === fallback || tz.group.includes(fallback));
  return tz ? timeZoneSelectOption(tz) : null;
});

const timeZoneChosen = computed<TimeZoneSelectOption | null>({
  get() {
    return timeZones.find((tz) => tz.value === timeZone.value) ?? null;
  },
  set(option) {
    timeZone.value = option?.value ?? null;
  },
});
</script>

<template>
  <Combobox
    v-bind="$attrs"
    v-model="timeZoneChosen"
    :options="timeZones"
    :toKey="(tz) => tz.value"
    :toLabel="(tz) => tz.label"
    placeholder="Select a time zone"
    searchPlaceholder="Search time zones..."
    class="w-full"
  >
    <template #selected="{ option }">
      {{ option.alternativeName }} - {{ option.city }} <span class="text-gray-500">UTC{{ option.offset }}</span>
    </template>
    <template #option="{ option }">
      <div class="flex min-w-0 flex-1 gap-3">
        <span class="w-[10ex] shrink-0 text-gray-500">UTC{{ option.offset }}</span>
        <span class="min-w-0 flex-1">
          {{ option.alternativeName }} - {{ option.mainCities }}
          <Badge v-if="defaultTimeZone === option.value" class="ml-1">Your time zone</Badge>
        </span>
        <span class="shrink-0 text-right">{{ ianaTimeZoneCurrentTime(option.value) }}</span>
      </div>
    </template>
  </Combobox>
  <p v-if="timeZoneFallback" class="mt-2 text-sm text-gray-600">
    No time zone is selected, so
    <template v-if="timeZoneFallbackOption">
      {{ timeZoneFallbackOption.alternativeName }} - {{ timeZoneFallbackOption.city }}
      <span class="text-gray-500">UTC{{ timeZoneFallbackOption.offset }}</span>
    </template>
    <template v-else>{{ timeZoneFallback }}</template>
    (currently {{ ianaTimeZoneCurrentTime(timeZoneFallback) }}) is being used based on the facility's state.
  </p>
</template>
